import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import {
  FieldTypeEnum,
  IClientCurrencySettings,
  IClientSettings,
  ILegalIdentifierSettings,
  IOrganizationalUnit,
  IOrganizationalUnitManagers,
  IViewers,
  addViewersToClientContactsUnit,
  addViewersToTeamsUnit,
  addViewersToUnit,
  autocompleteClientName,
  createOrganizationalUnitManagers,
  deleteOUManagerMember,
  deleteViewersFromClientContactsUnit,
  deleteViewersFromTeamsUnit,
  deleteViewersFromUnit,
  getSelectedOUClients,
  selectedOrganizationalUnitClientManagers,
  selectedOrganizationalUnitManagers,
  selectedOrganizationalUnitTeams,
} from 'wisestep-api';

import { IOption } from '../../../components/SettingsIndex';
import { TitleAndBreadCrumbs } from '../../../features/invoicing/helper/InvocieSettingsHelper';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';

export const clientSettingsIndexOptions: IOption[] = [
  { label: 'Currency', url: '/currencies' },
  { label: 'File Categories', url: '/app-settings/file_categories' },
  { label: 'Legal Identifiers', url: '/legal_identifiers' },
  { label: 'Fee Arrangements', url: '/position_levels' },
  { label: 'Customize Client Fields', url: '/app-settings/customize-client-fields' },
];

export const fieldTypes = [
  'TextBox',
  'Number',
  'Date',
  'Location',
  'DateAndTime',
  'URL',
  'Tags',
  'Dropdown',
  'Users',
  'Checkbox',
] as const;

export const FieldNameMap = {
  TextBox: 'TextBox',
  Number: 'Number',
  Date: 'Date',
  Location: 'Location',
  DateAndTime: 'Date And Time',
  URL: 'URL',
  Tags: 'Tags',
  Dropdown: 'Dropdown',
  Users: 'Users',
  Checkbox: 'Checkbox',
};

export const FieldIconMap = {
  TextBox: TextFieldsOutlinedIcon,
  Number: PinOutlinedIcon,
  Date: CalendarMonthOutlinedIcon,
  Location: LocationOnOutlinedIcon,
  DateAndTime: AccessTimeOutlinedIcon,
  URL: LinkOutlinedIcon,
  Tags: LocalOfferOutlinedIcon,
  Dropdown: DnsOutlinedIcon,
  Users: GroupOutlinedIcon,
  Checkbox: CheckBoxOutlinedIcon,
};

export const clientSettingsDefaultPayload: IClientSettings = {
  name: '',
  active: true,
  id: '',
  workspaceId: '',
  createdBy: '',
  createdOn: null,
  modifiedBy: '',
  modifiedOn: null,
  description: '',
};

export const legalIdentifierDefaultPayload: ILegalIdentifierSettings = {
  ...clientSettingsDefaultPayload,
  country: '',
};

export const OrganizationalUnitDefaultPayload: IOrganizationalUnit = {
  ...clientSettingsDefaultPayload,
  scopeType: '',
  selectedTeams: [],
};

export const currencySettingsDefaultQuery: IClientCurrencySettings = {
  ...clientSettingsDefaultPayload,
  symbol: '',
  active: true,
  default: false,
};

export const hardcodedClientFields = [
  {
    label: 'Parent Client',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Client Name',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Legal Name',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Industry Type',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Source',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Website',
    type: FieldTypeEnum.URL,
  },
  {
    label: 'Time Zone',
    type: FieldTypeEnum.DateAndTime,
  },
  {
    label: 'Invoice Code',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Payment Terms',
    type: FieldTypeEnum.Number,
  },
  {
    label: 'Branch Name',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Country',
    type: FieldTypeEnum.Location,
  },
  {
    label: 'Address',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Description',
    type: FieldTypeEnum.TextBox,
  },
].map((field) => ({ ...field, hardcoded: true }));

const stringToColor = (string: string) => {
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};
export const stringAvatar = (name: string) => {
  return {
    sx: {
      bgcolor: stringToColor(name),
      color: 'common.black',
      width: 30,
      height: 30,
      fontSize: 15,
    },
    children: `${name.split(' ')[0][0]}`,
  };
};

export const getClients = async (clientNameSearch: string) => {
  const result = await autocompleteClientName(`${encodeURIComponent(clientNameSearch)}`);
  const clients = result.data.map((c) => ({ ...c, id: c.value }));
  return clients;
};

export const tabstype = {
  Teams: 'Teams',
  'Account Managers': 'Account Managers',
  Clients: 'Clients',
  'Client Contacts': 'Client Contacts',
};
export const gobackUrl = {
  Teams: '/workspace-users-and-teams/organizational-unit-recruitment',
  'Account Managers': '/workspace-users-and-teams/account-manager-units',
  Clients: '/workspace-users-and-teams/client-units',
  'Client Contacts': '/workspace-users-and-teams/client-contacts-units',
};

export interface IClientOptions {
  id: string;
  name: string;
  status?: string;
}
export interface IClientCheckBoxListOptions {
  [key: string]: IClientOptions;
}

export enum MethodType {
  Get = 'GET',
  Add = 'ADD',
  Delete = 'DELETE',
}

export enum ScopeType {
  Teams = 'Teams',
  Clients = 'Clients',
  Account_Managers = 'Account Managers',
  Client_Contacts = 'Client Contacts',
}

export enum RoleTypes {
  User = 'User',
  Lead = 'Lead',
  Analyst = 'Analyst',
  Head = 'Head',
  ACCOUNT_MANAGER = 'Account Manager',
}

export const viewersApisCalls = async (
  scopeType: string,
  func: string,
  organizationalId?: string,
  payload?: IViewers
) => {
  const apis = {
    [ScopeType.Teams]: {
      [MethodType.Get]: () => selectedOrganizationalUnitTeams(organizationalId ?? ''),
      [MethodType.Add]: () => payload && addViewersToTeamsUnit(payload),
      [MethodType.Delete]: () => payload && deleteViewersFromTeamsUnit(payload),
    },
    [ScopeType.Clients]: {
      [MethodType.Get]: () => getSelectedOUClients(organizationalId ?? ''),
      [MethodType.Add]: () => payload && addViewersToUnit(payload),
      [MethodType.Delete]: () => payload && deleteViewersFromUnit(payload),
    },
    [ScopeType.Client_Contacts]: {
      [MethodType.Get]: () => selectedOrganizationalUnitClientManagers(organizationalId ?? ''),
      [MethodType.Add]: () => payload && addViewersToClientContactsUnit(payload),
      [MethodType.Delete]: () => payload && deleteViewersFromClientContactsUnit(payload),
    },
    [ScopeType.Account_Managers]: {
      [MethodType.Get]: () => selectedOrganizationalUnitManagers(organizationalId ?? ''),
      [MethodType.Add]: () => {
        if (!payload) return;
        const req: IOrganizationalUnitManagers[] = payload.viewersIds.map((item) => ({
          organizationalId: payload.organizationalId,
          accountManagerId: item,
          type: 'Viewers',
        }));
        return createOrganizationalUnitManagers(req);
      },
      [MethodType.Delete]: async () => {
        if (!payload) return;
        const res = await selectedOrganizationalUnitManagers(payload.organizationalId ?? '');
        const data: any[] = res?.data ?? [];
        const id = data.find((item) => item.accountManagerId === payload.viewersIds[0])?.value;
        return deleteOUManagerMember(id ?? '');
      },
    },
  };
  const apiFunc = apis?.[scopeType]?.[func];
  return await apiFunc();
};

export type ClientSettingType = 'FILE_CATAGORIES' | 'LEGAL_IDENTIFIERS';

export const ClientBreadCrumbListAndTitle: Record<ClientSettingType, TitleAndBreadCrumbs> = {
  FILE_CATAGORIES: {
    Title: 'File Catagories',
    BreadcrumbsList: getSettingBreadCrumbList(
      ISettingBreadCrumbTypes.CLIENTS,
      Pages['FILE_CATAGORIES']
    ),
  },
  LEGAL_IDENTIFIERS: {
    Title: 'Legal Identifiers',
    BreadcrumbsList: getSettingBreadCrumbList(
      ISettingBreadCrumbTypes.CLIENTS,
      Pages['LEGAL_IDENTIFIERS']
    ),
  },
};
